import { Icon } from "@fluentui/react";
import { ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AppConfig } from "../../Config";
import { IAlarm, isTestAlarm, testAlarmNote } from "../../Models/IAlarm";
import { ISensor } from "../../Models/ISensor";
import { ISentinelRich } from "../../Models/ISentinel";
import { GlobalState } from "../../Redux/RootReducer";
import {
  alarmTypesByPriority,
  canUseSetup,
  hhmm,
  sensorName,
  sinceLabel,
  nameLabel,
} from "../../Utils/DeviceTexts";
import { useSentinelAlarms } from "../../Utils/OpenAlarmsStore";
import { isSensorOffline } from "../../Utils/SensorStatus";
import TestBadge from "../TestBadge/TestBadge";
import "./DeviceCard.scss";

// Parti comuni alle card della Home (letto e varco): stato, fascia colorata e riapertura dell'allarme.

export type DeviceCardStatus = "alarm" | "test" | "offline" | "off" | "ok";

export interface IDeviceCardState {
  status: DeviceCardStatus;
  // allarmi aperti del dispositivo
  alarms: IAlarm[];
  // tipi di sensore con un allarme vero, dal piu' importante
  alarmTypes: string[];
  // tipi di sensore con solo allarmi di prova (gialli, non rossi)
  testTypes: string[];
  // ultimo dato del sensore che non risponde da piu' tempo
  offlineSince: string | null;
  // nessun sensore manda dati: Ascolta, Parla e Luce non possono funzionare
  silent: boolean;
}

// la bilancia (pesogr2) non ha un tondo sulla card: da sola non fa scattare «Non risponde»
export const isCardSensor = (x: ISensor) => x.type !== "pesogr2";

// stato della card: allarme > spento > non risponde > tutto ok
export const useDeviceCardState = (sentinel: ISentinelRich): IDeviceCardState => {
  const [, setTick] = useState<number>(0);
  const { alarms } = useSentinelAlarms(sentinel.id);

  // ridisegna ogni tanto: lo stato «non risponde» dipende dall'ora
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((prev: number) => prev + 1);
    }, AppConfig.sensor.offlineCheckInterval * 1000);
    return () => clearInterval(interval);
  }, []);

  const watched = sentinel.sensors.filter((x: ISensor) => x.enabled && isCardSensor(x));
  const offline = watched
    .filter((x: ISensor) => isSensorOffline(x))
    .map((x: ISensor) => x.updatedAt)
    .sort();
  const alarmTypes = alarmTypesByPriority(alarms.filter((x: IAlarm) => !isTestAlarm(x)));
  const testTypes = alarmTypesByPriority(alarms.filter((x: IAlarm) => isTestAlarm(x))).filter(
    (t: string) => !alarmTypes.includes(t)
  );

  let status: DeviceCardStatus = "ok";
  if (alarms.length > 0) {
    status = alarmTypes.length === 0 ? "test" : "alarm";
  } else if (!sentinel.enabled) {
    status = "off";
  } else if (offline.length > 0) {
    status = "offline";
  }

  return {
    status,
    alarms,
    alarmTypes,
    testTypes,
    offlineSince: offline.length > 0 ? offline[0] : null,
    silent: sentinel.enabled && watched.length > 0 && offline.length === watched.length,
  };
};

// meno di 2 minuti senza dati puo' essere un intoppo: niente durata
const OFFLINE_HINT_AFTER_MS = 2 * 60 * 1000;
export const OFFLINE_HINT = "Controlla corrente e Wi‑Fi.";

// suggerimento di sempre sul segno giallo, ora sulla fascia
const OFFLINE_TOOLTIP = {
  bed: "Uno o più sensori sono offline - Verificare alimentazione, connessione WiFi o malfunzionamento",
  gateway: "Sensore offline - Verificare alimentazione, connessione WiFi o malfunzionamento",
};

const DeviceCardBand = (props: { state: IDeviceCardState; kind: "bed" | "gateway" }) => {
  const user = useSelector((s: GlobalState) => s.generic.user);
  const { status, alarms, alarmTypes, offlineSince } = props.state;
  if (status === "ok") return null;

  if (status === "alarm") {
    const top = alarmTypes[0];
    const first = alarms
      .filter((x: IAlarm) => (x.sensor?.type ?? x.sensorType) === top)
      .map((x: IAlarm) => x.createdAt)
      .sort()[0];
    const others = alarmTypes.slice(1).map(sensorName);
    const text = "Allarme: " + sensorName(top) + " alle " + hhmm(first);
    return (
      <div
        className="device-card-band"
        role="status"
        title={others.length > 0 ? text + ", anche " + others.join(", ") : text}
      >
        <Icon iconName="Ringer" className="device-card-band-icon" />
        <span className="device-card-band-text">
          Allarme: <b>{sensorName(top)}</b> alle {hhmm(first)}
          {others.length > 0 && " +" + others.length}
        </span>
      </div>
    );
  }
  if (status === "test") {
    return (
      <div className="device-card-band" role="status" title={testAlarmNote(alarms[0])}>
        <TestBadge title="Allarme di prova lanciato da Setup: va gestito come gli altri" />
        <span className="device-card-band-text">Prova in corso</span>
      </div>
    );
  }
  if (status === "offline") {
    const long = offlineSince !== null && Date.now() - new Date(offlineSince).getTime() >= OFFLINE_HINT_AFTER_MS;
    const text = "Non risponde" + (long ? " da " + sinceLabel(offlineSince as string) : "");
    // il consiglio sta nel suggerimento e nei Dettagli: la fascia resta su una riga
    return (
      <div className="device-card-band" title={OFFLINE_TOOLTIP[props.kind]}>
        <Icon iconName="Warning" className="device-card-band-icon" />
        <span className="device-card-band-text">{text}</span>
      </div>
    );
  }
  const setup = canUseSetup(user);
  return (
    <div
      className="device-card-band"
      title={"Spento: non manda allarmi. " + (setup ? "Si riaccende dal Setup." : "Lo riaccende chi amministra.")}
    >
      <Icon iconName="PowerButton" className="device-card-band-icon" />
      <span className="device-card-band-text">{setup ? "Spento · si riaccende dal Setup" : "Spento"}</span>
    </div>
  );
};

// segno giallo al centro del pittogramma del dispositivo che non risponde (come prima)
export const DeviceCardOfflineMark = (props: { state: IDeviceCardState }) =>
  props.state.status === "offline" ? (
    <span className="device-card-offline-badge" aria-hidden="true">
      <span className="warning-triangle">⚠️</span>
    </span>
  ) : null;

// Ascolta, Parla e Luce passano dal dispositivo: se non risponde non possono funzionare
export const DEVICE_SILENT_TOOLTIP = "Non disponibile: il dispositivo non risponde";

export interface IDeviceCardProps {
  sentinel: ISentinelRich;
  state: IDeviceCardState;
  kind: "bed" | "gateway";
  children: ReactNode;
}

// Contenitore della card: bordo e fascia per stato
const DeviceCard = (props: IDeviceCardProps) => {
  return (
    <article
      className={"device-card device-card-" + props.kind + " st-" + props.state.status}
      aria-label={nameLabel(props.sentinel.patient.identifier)}
    >
      <DeviceCardBand state={props.state} kind={props.kind} />
      {props.children}
    </article>
  );
};

export default DeviceCard;
